import { SCHEDULE_LABELS, type ScraperInput, type ScraperSettings, type ScraperSummary } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Globe, Lock } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { LearnMore } from '../components/guidance/learn-more';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { MonitorForm } from '../components/scrapers/monitor-form';
import { MonitorStatus, when } from '../components/scrapers/monitor-status';
import { Button } from '../components/ui/button';
import { useOptionalConfirm } from '../components/ui/confirm';
import { ApiError, api } from '../lib/api';
import { MONITORS_KEY, type MonitorList } from '../lib/scrapers';

// Admin → Website monitors (task 40): pages or sections of websites checked on a schedule. Changed pages are stored
// as documents and processed like uploads. The switch at the top stops every check at once.

export const Route = createFileRoute('/admin/scrapers/')({ component: ScrapersPage });

const TOUR = {
  id: 'admin-scrapers',
  steps: [
    {
      target: '[data-tour="scraper-form"]',
      title: 'Watch a page',
      body: 'Give the address of a page to watch. Each time its text changes, the new version is stored in the Library and read like an upload.',
      learnMore: 'website-monitors',
    },
    {
      target: '[data-tour="scraper-list"]',
      title: 'Checks and changes',
      body: 'Each monitor shows its last check and what it found. Open one to see what changed on each page, or check it now.',
    },
  ],
};

function ScrapersPage() {
  useScreenTour(TOUR);
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const confirm = useOptionalConfirm();
  const q = useQuery({ queryKey: MONITORS_KEY, queryFn: () => api<MonitorList>('/admin/scrapers') });
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const onError = (e: unknown) => {
    if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
    setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  };
  const refresh = () => qc.invalidateQueries({ queryKey: MONITORS_KEY });

  const create = useMutation({
    mutationFn: (input: ScraperInput) => withReauth(() => api<{ scraper: ScraperSummary }>('/admin/scrapers', { body: input })),
    onSuccess: r => {
      setMessage({
        kind: 'ok',
        text:
          r.scraper.schedule === 'manual'
            ? `Created “${r.scraper.name}”. Open it and press Check now to read the page.`
            : `Created “${r.scraper.name}”. Its first check runs within half an hour, or open it and press Check now.`,
      });
      setFormKey(k => k + 1);
      refresh();
    },
    onError,
  });
  const run = useMutation({
    mutationFn: (m: ScraperSummary) => withReauth(() => api<{ scraper: ScraperSummary }>(`/admin/scrapers/${m.id}/run`, { body: {} })),
    onMutate: () => setMessage(null),
    onSuccess: () => refresh(),
    onSettled: () => refresh(),
    onError,
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      withReauth(() => api<ScraperSettings>('/admin/scraper-settings', { method: 'PUT', body: { enabled } })),
    onSuccess: s => {
      setMessage({
        kind: 'ok',
        text: s.enabled
          ? 'Website monitors are on again. Overdue checks run at the next pass.'
          : 'Website monitors are off. No page is checked until you switch them on.',
      });
      refresh();
    },
    onError,
  });

  const askToggle = async (enabled: boolean) => {
    setMessage(null);
    if (!enabled && confirm) {
      const ok = await confirm({
        title: 'Switch off all website monitors?',
        body: 'No page is checked, on schedule or by hand, until they are switched on again. The pages already stored stay.',
        confirmLabel: 'Switch off',
        cancelLabel: 'Keep them on',
        destructive: true,
      });
      if (!ok) return;
    }
    toggle.mutate(enabled);
  };

  if (q.isError)
    return (
      <>
        <PageHeader title="Website monitors" />
        <ErrorState
          error={q.error}
          title="The website monitors couldn’t be loaded"
          onRetry={() => q.refetch()}
          learnMore="website-monitors"
        />
      </>
    );
  const monitors = q.data?.scrapers ?? [];
  const settings = q.data?.settings;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Website monitors"
        description="Web pages checked on a schedule. When a page's text changes, the new version is stored in the Library and processed like an upload."
      />
      <Tip id="scrapers-data" learnMore="website-monitors">
        Pages are stored as evidence of what a site said and when. Their text is read as data: nothing on a page can instruct Hoopoe.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      {settings && (
        <section
          aria-labelledby="switch-title"
          className="flex flex-wrap items-center gap-3 rounded-lg border bg-surface-raised p-4 text-sm"
        >
          <div className="flex-1 space-y-1">
            <h2 id="switch-title" className="font-semibold">
              {settings.enabled ? 'Monitors are on' : 'Monitors are switched off'}
            </h2>
            <p className="text-muted-foreground">
              {settings.enabled
                ? 'Due monitors are checked every half hour.'
                : 'No page is checked, on schedule or by hand, until monitors are switched on again.'}
              {!settings.browserAvailable && ' Browser Rendering isn’t available here, so pages are read as sites send them.'}
            </p>
          </div>
          <Button
            size="sm"
            variant={settings.enabled ? 'outline' : 'primary'}
            disabled={toggle.isPending}
            onClick={() => askToggle(!settings.enabled)}
          >
            {settings.enabled ? 'Switch off all monitors' : 'Switch monitors on'}
          </Button>
        </section>
      )}

      <section aria-labelledby="form-title" className="space-y-4" data-tour="scraper-form">
        <h2 id="form-title" className="font-semibold">
          New monitor
        </h2>
        <MonitorForm
          key={formKey}
          titleId="form-title"
          pending={create.isPending}
          browserAvailable={settings?.browserAvailable ?? true}
          onSubmit={input => {
            setMessage(null);
            create.mutate(input);
          }}
        />
      </section>

      <section aria-labelledby="monitors-title" className="space-y-3" data-tour="scraper-list">
        <h2 id="monitors-title" className="font-semibold">
          Monitors
        </h2>
        {!q.data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : monitors.length === 0 ? (
          <EmptyState icon={Globe} title="No website monitors yet" learnMore="website-monitors">
            Create one for each page or site section to follow, such as a ministry’s press releases or a party’s statements.
          </EmptyState>
        ) : (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Website monitors">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Monitor</th>
                  <th className="p-2 text-start font-medium">How often</th>
                  <th className="p-2 text-start font-medium">Last check</th>
                  <th className="p-2 text-start font-medium">Found</th>
                  <th className="p-2 text-start font-medium">Next check</th>
                  <th className="p-2 text-start font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {monitors.map(m => (
                  <tr key={m.id} className="border-b last:border-0" aria-label={m.name}>
                    <td className="max-w-xs p-2">
                      <Link to="/admin/scrapers/$id" params={{ id: m.id }} className="font-medium underline-offset-2 hover:underline">
                        <DataText>{m.name}</DataText>
                      </Link>
                      {m.sensitivity === 'confidential' && (
                        <span className="ms-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Lock className="size-3" aria-hidden="true" /> Confidential
                        </span>
                      )}
                      <span className="block truncate text-xs text-muted-foreground" dir="ltr">
                        {m.url}
                      </span>
                    </td>
                    <td className="p-2">
                      {SCHEDULE_LABELS[m.schedule]}
                      {m.scope === 'section' && (
                        <span className="block text-xs text-muted-foreground">
                          up to <DataNumber>{m.maxPages}</DataNumber> pages
                        </span>
                      )}
                    </td>
                    <td className="p-2" data-testid="monitor-status">
                      <MonitorStatus monitor={m} />
                    </td>
                    <td className="p-2">
                      <DataNumber>{m.changes.toLocaleString()}</DataNumber> {m.changes === 1 ? 'version' : 'versions'}
                      <span className="block text-xs text-muted-foreground">
                        of <DataNumber>{m.pages.toLocaleString()}</DataNumber> {m.pages === 1 ? 'page' : 'pages'}
                      </span>
                    </td>
                    <td className="p-2">
                      <DataNumber>{m.enabled ? (m.nextRunAt ? when(m.nextRunAt) : 'By hand only') : 'Paused'}</DataNumber>
                    </td>
                    <td className="p-2">
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          context={`for ${m.name}`}
                          disabled={!settings?.enabled || m.running || (run.isPending && run.variables?.id === m.id)}
                          onClick={() => run.mutate(m)}
                        >
                          {m.running || (run.isPending && run.variables?.id === m.id) ? 'Checking…' : 'Check now'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <LearnMore slug="website-monitors" />
      </section>
    </div>
  );
}
